import { useEffect, useState } from "react";
import api, { API } from "../lib/api";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogTrigger, DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import {
  Share2, Copy, Trash2, ShieldCheck, Plus, Eye, FileDown, Clock, Lock,
} from "lucide-react";
import { toast } from "sonner";

const publicUrl = (token) => `${window.location.origin}/share/${token}`;

function statusStyle(status) {
  if (status === "active") return { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", label: "ACTIVE" };
  if (status === "expired") return { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", label: "EXPIRED" };
  return { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", label: "REVOKED" };
}

function relative(iso) {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

export default function ShareReportDialog({ pid }) {
  const [open, setOpen] = useState(false);
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState("");
  const [expiry, setExpiry] = useState("never");
  const [password, setPassword] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/programs/${pid}/shares`);
      setShares(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) refresh();
  }, [open, pid]);

  const create = async () => {
    setCreating(true);
    try {
      const body = { label: label.trim() || null };
      if (expiry !== "never") body.days = parseInt(expiry, 10);
      if (password.trim()) body.password = password.trim();
      await api.post(`/programs/${pid}/share`, body);
      setLabel("");
      setPassword("");
      setExpiry("never");
      toast.success("Share link created");
      await refresh();
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (token) => {
    if (!window.confirm("Revoke this link? Anyone with the URL will lose access immediately.")) return;
    try {
      await api.delete(`/programs/${pid}/shares/${token}`);
      toast.success("Link revoked");
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't revoke");
    }
  };

  const copy = async (token) => {
    try {
      await navigator.clipboard.writeText(publicUrl(token));
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Couldn't copy — please copy manually");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-slate-300" data-testid="share-report-button">
          <Share2 className="w-4 h-4 mr-2" /> Share
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl" data-testid="share-settings-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Share settings</DialogTitle>
          <DialogDescription>
            Create one or more public, view-only links for this program. Each link works without an account. Revoke instantly to cut access.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-slate-50 border border-slate-200 rounded-md p-4 mb-1" data-testid="share-new-link-form">
          <p className="label-overline mb-3 flex items-center gap-2"><Plus className="w-3.5 h-3.5" /> New link</p>
          <div className="grid sm:grid-cols-3 gap-3">
            <Input
              placeholder="Label (e.g. Donor — Q3 2025)"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="text-sm"
              data-testid="share-label-input"
            />
            <Select value={expiry} onValueChange={setExpiry}>
              <SelectTrigger className="text-sm" data-testid="share-expiry-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="never">No expiry</SelectItem>
                <SelectItem value="7">7 days</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="password"
              placeholder="Password (optional)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="text-sm"
              data-testid="share-password-input"
            />
          </div>
          <Button
            onClick={create}
            disabled={creating}
            className="mt-3 bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="share-create-button"
          >
            {creating ? "Creating…" : "Create link"}
          </Button>
        </div>

        <div className="border-t border-slate-200 pt-4 mt-3">
          <p className="label-overline mb-3">Active links · {shares.length}</p>
          {loading ? (
            <p className="text-sm text-slate-400">Loading…</p>
          ) : shares.length === 0 ? (
            <div className="text-center py-8 text-sm text-slate-400" data-testid="share-empty">
              No active links yet. Create one above to start sharing.
            </div>
          ) : (
            <ul className="space-y-3" data-testid="share-list">
              {shares.map((s) => {
                const st = statusStyle(s.status);
                return (
                  <li key={s.token} className="border border-slate-200 rounded-sm p-3" data-testid={`share-row-${s.token}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-slate-800 truncate">
                            {s.label || "Untitled link"}
                          </span>
                          <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${st.bg} ${st.text} ${st.border}`}>{st.label}</span>
                          {s.has_password && (
                            <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 flex items-center gap-0.5"><Lock className="w-3 h-3" /> protected</span>
                          )}
                        </div>
                        <p className="mt-1 text-xs font-mono text-slate-500 truncate" data-testid={`share-url-${s.token}`}>
                          {publicUrl(s.token)}
                        </p>
                        <div className="mt-2 flex items-center gap-4 text-xs text-slate-500 flex-wrap">
                          <span className="flex items-center gap-1" data-testid={`share-views-${s.token}`}>
                            <Eye className="w-3 h-3" /> {s.views} view{s.views === 1 ? "" : "s"}
                          </span>
                          <span className="flex items-center gap-1 text-emerald-700" data-testid={`share-uniques-${s.token}`} title="Distinct visitors (privacy-respecting; raw IPs never stored)">
                            · {s.unique_visitors || 0} unique visitor{(s.unique_visitors || 0) === 1 ? "" : "s"}
                          </span>
                          <span className="flex items-center gap-1"><FileDown className="w-3 h-3" /> {s.pdf_downloads} PDFs</span>
                          {s.last_opened_at && (
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {relative(s.last_opened_at)}</span>
                          )}
                          {s.expires_at && (
                            <span className="flex items-center gap-1 text-amber-700">expires {new Date(s.expires_at).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copy(s.token)}
                          data-testid={`share-copy-${s.token}`}
                          disabled={s.status !== "active"}
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => revoke(s.token)}
                          className="text-rose-600 hover:text-rose-700 border-rose-200"
                          data-testid={`share-revoke-${s.token}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <DialogFooter>
          <div className="text-xs text-slate-500 flex items-center gap-2 mr-auto">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> Public viewers see only published numbers + audit drill-downs.
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
